import { useState } from "react";
import { Button, Form, Input, Select, Spin, Tabs } from "antd";
import CustomForm from "../../CustomComponents/CustomForm";
import { theme } from "../../theme";
import {
	NOTIFICATION_TYPES,
	openNotification,
} from "../Notifications/NotificationsUtils";
import { Link, useHistory } from "react-router-dom";
import {
	fetchCompanyData,
	registerCompany,
} from "../../Requests/company-requests";
import { useTranslation } from "react-i18next";
import { faxPattern, phonePattern, normalizeCif } from "../../utils/constants";
import { ErrorCodes } from "../../Api";
import { getErrorFromResponse } from "../../utils/responseUtils";
import SimpleLayout from "../../layouts/Simple";
import TabPane from "antd/es/tabs/TabPane";
import { registerExternal } from "../../Requests/academic-user-requests";
import { useFeatureFlags } from "../../utils/featureFlags";
import { Routes } from "../../utils/routes";

const { Option } = Select;

export interface companyState {
	name: string;
	cif: string;
	address: string;
	phone: string;
	fax: string;
	activityDomain: string;
	representativeName: string;
	email: string;
	password: string;
	passwordConfirm: string;
}

const Register = () => {
	const { t } = useTranslation();
	const history = useHistory();
	const [companyRegisterForm] = Form.useForm();
	const [companyRegisterState, setCompanyRegisterState] = useState({
		name: "" as any,
		commercialName: "" as any,
		cif: "" as any,
		address: "" as any,
		postalCode: "" as any,
		phone: "" as any,
		activityDomain: "" as any,
		fax: "" as any,
		representativeName: "" as any,
		email: "" as any,
		password: "" as any,
		passwordConfirm: "",
		preferredLanguage: "ro" as any,
		firstName: "" as any,
		lastName: "" as any,
	});

	const [externalRegisterForm] = Form.useForm();
	const [externalRegisterState, setExternalRegisterState] = useState({
		firstName: "" as any,
		lastName: "" as any,
		email: "" as any,
		password: "" as any,
		passwordConfirm: "",
	});

	const [step, setStep] = useState(1);
	const [spinning, setSpinning] = useState(false);
	const [cleanedCif, setCleanedCif] = useState("");
	const [activeTab, setActiveTab] = useState("1");
	const { isPoliJobsMarketEnabled } = useFeatureFlags();

	const validateRomanianCif = (cif: string): boolean => {
		cif = cif.toUpperCase().replace("RO", "");

		if (isNaN(Number(cif))) return false;
		if (cif.length > 10) return false;

		let cifra_control = cif.slice(-1);
		cif = cif.slice(0, -1);

		while (cif.length !== 9) {
			cif = "0" + cif;
		}

		let suma =
			Number(cif[0]) * 7 +
			Number(cif[1]) * 5 +
			Number(cif[2]) * 3 +
			Number(cif[3]) * 2 +
			Number(cif[4]) * 1 +
			Number(cif[5]) * 7 +
			Number(cif[6]) * 5 +
			Number(cif[7]) * 3 +
			Number(cif[8]) * 2;
		suma = suma * 10;

		let rest = suma % 11;

		if (rest === 10) rest = 0;

		return rest === Number(cifra_control);
	};

	const changeTab = (activeKey: any) => {
		setActiveTab(activeKey);
	};

	const fetchData = async () => {
		const newCleanedCif = normalizeCif(companyRegisterState.cif);
		setCleanedCif(newCleanedCif);

		const cui = parseInt(newCleanedCif);
		fetchCompanyData(cui)
			.then((response) => {
				setCompanyRegisterState((prevState) => {
					return {
						...prevState,
						name: response.response?.denumire,
						address: response.response?.adresa,
						phone: response.response?.telefon,
						fax: response.response?.fax,
					};
				});

				companyRegisterForm.setFieldsValue({
					name: response.response?.denumire,
					cif: companyRegisterState.cif,
					address: response.response?.adresa,
					phone: response.response?.telefon,
					fax: response.response?.fax,
				});

				if (response.response?.denumire !== "") setStep(2);
				else
					openNotification(
						t("signupText.messages.error"),
						t("signupText.messages.companyNotFound"),
						NOTIFICATION_TYPES.ERROR
					);
			})
			.catch(async (_error) => {
				const errorMessage = await getErrorFromResponse(_error);

				if (errorMessage?.code === ErrorCodes.AnafApiUnavailable) {
					openNotification(
						t("signupText.messages.error"),
						t("signupText.messages.anafApiUnavailable"),
						NOTIFICATION_TYPES.INFO
					);

					companyRegisterForm.setFieldsValue({
						cif: companyRegisterState.cif,
					});

					setStep(2);
				} else {
					openNotification(
						t("signupText.messages.error"),
						t("signupText.messages.fetchCompanyDataError"),
						NOTIFICATION_TYPES.ERROR
					);
				}
			});
	};

	const handleChangeCompanyRegister = (event: any, field: any) => {
		event.persist();

		setCompanyRegisterState((prevState) => {
			const newValue = event.target.value;
			const updatedState = {
				...prevState,
				[field]: newValue,
			};

			if (field === "name" && (!newValue || newValue.trim() === "")) {
				updatedState.commercialName = newValue;
			}

			return updatedState;
		});
	};

	const handleChangeExternalRegister = (event: any, field: any) => {
		event.persist();

		setExternalRegisterState((prevState) => {
			return {
				...prevState,
				[field]: event.target.value,
			};
		});
	};

	const changeLanguage = (lng: any) => {
		setCompanyRegisterState((prevState) => {
			return {
				...prevState,
				preferredLanguage: lng,
			};
		});
	};

	const verifyCredentialsCompany = (event: any) => {
		if (event.key === "Enter") {
			handleRegisterCompany();
		}
	};

	const handleRegisterCompany = () => {
		companyRegisterForm.validateFields().then(() => {
			setSpinning(true);
			const normalizedCif = normalizeCif(companyRegisterState.cif);

			const reqBody = {
				name: companyRegisterState.name,
				commercialName: companyRegisterState.commercialName,
				cui: normalizedCif,
				address: companyRegisterState.address,
				postalCode: companyRegisterState.postalCode,
				phone: companyRegisterState.phone,
				domainOfActivity: companyRegisterState.activityDomain,
				fax: companyRegisterState.fax,
				repName: companyRegisterState.representativeName,
				email: companyRegisterState.email,
				password: companyRegisterState.password,
				preferredLanguage: companyRegisterState.preferredLanguage,
			};

			registerCompany(reqBody)
				.then(() => {
					openNotification(
						t("signupText.messages.successRegister"),
						t("signupText.messages.successRegisterMessage"),
						NOTIFICATION_TYPES.SUCCESS,
						10
					);

					history.push("/autentificare");
				})
				.catch(async (error: any) => {
					const errorMessage = await getErrorFromResponse(error);

					let notificationMessage;

					switch (errorMessage?.code) {
						case ErrorCodes.CompanyAlreadyExists:
							notificationMessage = t("signupText.messages.duplicatedCIF");
							break;
						case ErrorCodes.CompanyUserAlreadyExists:
							notificationMessage = t("signupText.messages.userExists");
							break;
						default:
							notificationMessage = t("signupText.messages.failRegister");
							break;
					}

					openNotification(
						t("signupText.register"),
						notificationMessage,
						NOTIFICATION_TYPES.ERROR
					);
				})
				.finally(() => setSpinning(false));
		});
	};

	const verifyCredentialsExternal = (event: any) => {
		if (event.key === "Enter") {
			handleRegisterExternal();
		}
	};

	const handleRegisterExternal = () => {
		externalRegisterForm.validateFields().then(() => {
			setSpinning(true);

			const reqBody = {
				firstName: externalRegisterState.firstName,
				lastName: externalRegisterState.lastName,
				email: externalRegisterState.email,
				password: externalRegisterState.password,
			};

			registerExternal(reqBody)
				.then(() => {
					openNotification(
						t("signupText.messages.successRegister"),
						t("signupText.messages.successRegisterMessage"),
						NOTIFICATION_TYPES.SUCCESS,
						10
					);

					history.push("/autentificare");
				})
				.catch(async (error: any) => {
					const errorMessage = await getErrorFromResponse(error);

					let notificationMessage;

					switch (errorMessage?.code) {
						case ErrorCodes.AcademicUserAlreadyExists:
							notificationMessage = t("signupText.messages.userExists");
							break;
						default:
							notificationMessage = t("signupText.messages.failRegister");
							break;
					}

					openNotification(
						t("signupText.register"),
						notificationMessage,
						NOTIFICATION_TYPES.ERROR
					);
				})
				.finally(() => setSpinning(false));
		});
	};

	return (
		<SimpleLayout>
			<div className="flex flex-col md:min-h-0 md:h-auto min-h-[100dvh] overflow-y-auto justify-center">
				<Tabs
					tabBarStyle={{
						padding: "0px 1rem",
					}}
					activeKey={activeTab}
					onChange={changeTab}
					className="mx-auto mb-10 flex flex-col items-center gap-10"
				>
					<TabPane tab={t("signupText.company")} key="1">
						<div className="container mx-auto flex flex-col items-center gap-10">
							<div className="italic text-sm mb-5">
								{t("signupText.companySignupWarning")}
							</div>
							{step === 1 && (
								<Form.Provider onFormFinish={fetchData}>
									<CustomForm
										form={companyRegisterForm}
										layout={theme.layout}
										widthelem={"70%"}
									>
										<Form.Item
											className="w-full max-w-sm"
											name="cif"
											label={t("signupText.cif") + ":"}
											rules={[
												{
													required: true,
													message: t("usersText.requiredField", {
														field: t("signupText.cif"),
													}),
												},
												{
													validator: (_, value) => {
														if (value && !validateRomanianCif(value)) {
															return Promise.reject(
																new Error(
																	t("usersText.invalidField", {
																		field: t("signupText.cif"),
																	})
																)
															);
														}
														return Promise.resolve();
													},
												},
											]}
										>
											<Input
												onChange={(event: any) =>
													handleChangeCompanyRegister(event, "cif")
												}
											/>
										</Form.Item>

										<Button onClick={() => companyRegisterForm.submit()}>
											{t("signupText.continueButton")}
										</Button>
									</CustomForm>
								</Form.Provider>
							)}
							{step === 2 && (
								<Form.Provider>
									<CustomForm
										form={companyRegisterForm}
										layout={theme.layout}
										widthelem={"70%"}
										className="max-w-sm"
									>
										<Form.Item
											className="w-full"
											name="name"
											label={t("signupText.companyName") + ":"}
											rules={[
												{
													required: true,
													message: t("usersText.requiredField", {
														field: t("signupText.companyName"),
													}),
													whitespace: true,
												},
											]}
										>
											<Input
												onChange={(event: any) =>
													handleChangeCompanyRegister(event, "name")
												}
												disabled={true}
											/>
										</Form.Item>

										<Form.Item
											className="w-full"
											name="commercialName"
											label={t("signupText.commercialName") + ":"}
										>
											<Input
												autoComplete="new-password"
												onChange={(event: any) =>
													handleChangeCompanyRegister(event, "commercialName")
												}
											/>
										</Form.Item>

										<Form.Item
											className="w-full"
											name="cif"
											label={t("signupText.cif") + ":"}
										>
											<Input
												onChange={(event: any) =>
													handleChangeCompanyRegister(event, "cif")
												}
												disabled={true}
											/>
										</Form.Item>

										<Form.Item
											className="w-full"
											name="address"
											label={t("signupText.address") + ":"}
											rules={[
												{
													required: true,
													message: t("usersText.requiredField", {
														field: t("signupText.address"),
													}),
													whitespace: true,
												},
											]}
										>
											<Input
												onChange={(event: any) =>
													handleChangeCompanyRegister(event, "address")
												}
											/>
										</Form.Item>

										<Form.Item
											className="w-full"
											name="postalCode"
											label={t("signupText.postalCode") + ":"}
											rules={[
												{
													required: true,
													message: t("usersText.requiredField", {
														field: t("signupText.postalCode"),
													}),
													whitespace: true,
												},
											]}
										>
											<Input
												onChange={(event: any) =>
													handleChangeCompanyRegister(event, "postalCode")
												}
											/>
										</Form.Item>

										<Form.Item
											className="w-full"
											name="phone"
											label={t("signupText.phone") + ":"}
											rules={[
												{
													pattern: phonePattern,
													message: t("usersText.invalidField", {
														field: t("account.phone"),
													}),
												},
												{
													required: true,
													message: t("usersText.requiredField", {
														field: t("signupText.phone"),
													}),
													whitespace: true,
												},
											]}
										>
											<Input
												onChange={(event: any) =>
													handleChangeCompanyRegister(event, "phone")
												}
											/>
										</Form.Item>

										<Form.Item
											className="w-full"
											name="fax"
											label={t("signupText.fax") + ":"}
											rules={[
												{
													pattern: faxPattern,
													message: t("usersText.invalidField", {
														field: t("signupText.fax"),
													}),
												},
											]}
										>
											<Input
												onChange={(event: any) =>
													handleChangeCompanyRegister(event, "fax")
												}
											/>
										</Form.Item>

										<Form.Item
											className="w-full"
											name="activityDomain"
											label={t("signupText.activityDomain") + ":"}
											rules={[
												{
													required: true,
													message: t("usersText.requiredField", {
														field: t("signupText.activityDomain"),
													}),
													whitespace: true,
												},
											]}
										>
											<Input
												onChange={(event: any) =>
													handleChangeCompanyRegister(event, "activityDomain")
												}
											/>
										</Form.Item>

										<Form.Item
											className="w-full"
											name="representativeName"
											label={t("signupText.companyRepresentativeName") + ":"}
											rules={[
												{
													required: true,
													message: t("usersText.requiredField", {
														field: t("signupText.companyRepresentativeName"),
													}),
													whitespace: true,
												},
											]}
										>
											<Input
												onChange={(event: any) =>
													handleChangeCompanyRegister(
														event,
														"representativeName"
													)
												}
											/>
										</Form.Item>

										<Form.Item
											className="w-full"
											name="email"
											label={t("signupText.companyRepresentativeEmail") + ":"}
											rules={[
												{
													type: "email",
													message: t("usersText.invalidField", {
														field: t("signupText.companyRepresentativeEmail"),
													}),
												},
												{
													required: true,
													message: t("usersText.requiredField", {
														field: t("signupText.companyRepresentativeEmail"),
													}),
												},
											]}
										>
											<Input
												onChange={(event: any) =>
													handleChangeCompanyRegister(event, "email")
												}
											/>
										</Form.Item>

										<Form.Item
											className="w-full"
											name="preferredLanguage"
											initialValue={"ro"}
											label={t("account.preferredLanguage") + ":"}
											rules={[
												{
													whitespace: true,
												},
											]}
										>
											<Select onChange={(event: any) => changeLanguage(event)}>
												<Option value="ro">{t("account.romanian")}</Option>
												<Option value="en">{t("account.english")}</Option>
											</Select>
										</Form.Item>

										<Form.Item
											className="w-full"
											name="password"
											label={t("signupText.password") + ":"}
											rules={[
												{
													required: true,
													message: t("usersText.requiredField", {
														field: t("signupText.password"),
													}),
												},
												{
													min: 4,
													message: t("signupText.messages.lengthPasswordError"),
												},
											]}
										>
											<Input.Password
												onChange={(event: any) =>
													handleChangeCompanyRegister(event, "password")
												}
												onKeyPress={verifyCredentialsCompany}
											/>
										</Form.Item>

										<Form.Item
											className="w-full"
											name="passwordConfirm"
											label={t("signupText.confirmPassword") + ":"}
											rules={[
												{
													required: true,
													message: t("usersText.requiredField", {
														field: t("signupText.confirmPassword"),
													}),
												},
												({ getFieldValue }) => ({
													validator(_, value) {
														if (!value || getFieldValue("password") === value) {
															return Promise.resolve();
														}
														return Promise.reject(
															new Error(
																t("signupText.messages.mismatchedPasswords")
															)
														);
													},
												}),
											]}
										>
											<Input.Password
												onChange={(event: any) =>
													handleChangeCompanyRegister(event, "passwordConfirm")
												}
												onKeyPress={verifyCredentialsCompany}
											/>
										</Form.Item>

										<Button onClick={handleRegisterCompany}>
											{t("signupText.register")} <Spin spinning={spinning} />
										</Button>

										<Button className="my-6" onClick={() => setStep(1)}>
											{t("signupText.back")}
										</Button>
									</CustomForm>
								</Form.Provider>
							)}
						</div>
						<div className="flex justify-center mt-4">
							<Link to={Routes.LOGIN}>{t("signupText.alreadyRegistered")}</Link>
						</div>
					</TabPane>
					{isPoliJobsMarketEnabled && (
						<TabPane tab={t("signupText.externalUser")} key="2">
							<div className="container mx-auto flex flex-col items-center gap-10">
								<div className="italic text-sm mb-5">
									{t("signupText.externalSignupWarning")}
								</div>
								<Form.Provider>
									<CustomForm
										form={externalRegisterForm}
										layout={theme.layout}
										widthelem={"70%"}
										className="max-w-sm"
									>
										<Form.Item
											className="w-full"
											name="firstName"
											label={t("signupText.externalFirstName") + ":"}
											rules={[
												{
													required: true,
													message: t("usersText.requiredField", {
														field: t("signupText.externalFirstName"),
													}),
													whitespace: true,
												},
											]}
										>
											<Input
												onChange={(event: any) =>
													handleChangeExternalRegister(event, "firstName")
												}
											/>
										</Form.Item>

										<Form.Item
											className="w-full"
											name="lastName"
											label={t("signupText.externalLastName") + ":"}
											rules={[
												{
													required: true,
													message: t("usersText.requiredField", {
														field: t("signupText.externalLastName"),
													}),
													whitespace: true,
												},
											]}
										>
											<Input
												onChange={(event: any) =>
													handleChangeExternalRegister(event, "lastName")
												}
											/>
										</Form.Item>

										<Form.Item
											className="w-full"
											name="email"
											label={t("signupText.externalEmail") + ":"}
											rules={[
												{
													type: "email",
													message: t("usersText.invalidField", {
														field: t("signupText.externalEmail"),
													}),
												},
												{
													required: true,
													message: t("usersText.requiredField", {
														field: t("signupText.externalEmail"),
													}),
												},
											]}
										>
											<Input
												onChange={(event: any) =>
													handleChangeExternalRegister(event, "email")
												}
											/>
										</Form.Item>

										<Form.Item
											className="w-full"
											name="password"
											label={t("signupText.password") + ":"}
											rules={[
												{
													required: true,
													message: t("usersText.requiredField", {
														field: t("signupText.password"),
													}),
												},
												{
													min: 4,
													message: t("signupText.messages.lengthPasswordError"),
												},
											]}
										>
											<Input.Password
												onChange={(event: any) =>
													handleChangeExternalRegister(event, "password")
												}
												onKeyPress={verifyCredentialsExternal}
											/>
										</Form.Item>

										<Form.Item
											className="w-full"
											name="passwordConfirm"
											label={t("signupText.confirmPassword") + ":"}
											rules={[
												{
													required: true,
													message: t("usersText.requiredField", {
														field: t("signupText.confirmPassword"),
													}),
												},
												({ getFieldValue }) => ({
													validator(_, value) {
														if (!value || getFieldValue("password") === value) {
															return Promise.resolve();
														}
														return Promise.reject(
															new Error(
																t("signupText.messages.mismatchedPasswords")
															)
														);
													},
												}),
											]}
										>
											<Input.Password
												onChange={(event: any) =>
													handleChangeExternalRegister(event, "passwordConfirm")
												}
												onKeyPress={verifyCredentialsExternal}
											/>
										</Form.Item>

										<Button onClick={handleRegisterExternal}>
											{t("signupText.register")} <Spin spinning={spinning} />
										</Button>
									</CustomForm>
								</Form.Provider>
							</div>
							<div className="flex justify-center mt-4">
								<Link to={Routes.LOGIN}>
									{t("signupText.alreadyRegistered")}
								</Link>
							</div>
						</TabPane>
					)}
				</Tabs>
			</div>
		</SimpleLayout>
	);
};

export default Register;
